import { useEffect, useId, useRef, type FormEvent, type ReactNode } from "react";
import { AlertCircle, ShieldCheck } from "lucide-react";
import { FormRequirements, type Requirement } from "./FormRequirements";

interface GuardedChangeFormProps {
  /** Names the change in the operator's words, e.g. "Pause the app". */
  title: string;
  /** What happens to people using the app the moment this is applied. */
  impact: ReactNode;
  tone?: "danger";
  /** Exact phrase the gateway requires; also drives the confirmation field. */
  phrase: string;
  reason: string;
  onReasonChange: (value: string) => void;
  reasonPlaceholder: string;
  confirmation: string;
  onConfirmationChange: (value: string) => void;
  /** Fields belonging to one control only, rendered above the shared ones. */
  children?: ReactNode;
  /** Conditions the caller owns, listed alongside the shared ones when unmet. */
  requirements?: Requirement[];
  submitLabel: string;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (event: FormEvent) => void;
}

/**
 * The second half of every control on this page: a change has been chosen, and
 * this states its effect and collects the reason plus typed confirmation the
 * gateway demands. Keeping both controls on one component means an operator
 * learns the confirm step once.
 */
export function GuardedChangeForm({
  title, impact, tone, phrase, reason, onReasonChange, reasonPlaceholder,
  confirmation, onConfirmationChange, children, requirements = [],
  submitLabel, saving, error, onCancel, onSubmit
}: GuardedChangeFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const titleId = useId();
  const reasonMet = reason.trim().length >= 3;
  const phraseMet = phrase.length > 0 && confirmation === phrase;
  const outstanding = requirements.some((requirement) => !requirement.met);

  // The form replaces the action button in place, so move focus with it.
  useEffect(() => { formRef.current?.focus(); }, []);

  return (
    <form
      className={tone === "danger" ? "guarded-change is-danger" : "guarded-change"}
      ref={formRef}
      tabIndex={-1}
      aria-labelledby={titleId}
      onSubmit={onSubmit}
    >
      <div className="guarded-change-heading">
        <span className="guarded-change-icon"><ShieldCheck size={18} aria-hidden="true" /></span>
        <div>
          <p className="section-label">CONFIRM THIS CHANGE</p>
          <h3 id={titleId}>{title}</h3>
        </div>
      </div>
      <p className="guarded-change-impact">{impact}</p>
      {error ? <p className="guarded-change-error" role="alert"><AlertCircle size={16} aria-hidden="true" />{error}</p> : null}
      {children}
      <label className="stacked-field">
        Why are you making this change?
        <textarea rows={2} value={reason} onChange={(event) => onReasonChange(event.target.value)} placeholder={reasonPlaceholder} />
        <small>Saved to the audit log next to your name.</small>
      </label>
      <label className="stacked-field confirmation-field">
        <span>Type <strong>{phrase}</strong> to confirm</span>
        <input value={confirmation} onChange={(event) => onConfirmationChange(event.target.value)} autoComplete="off" spellCheck={false} />
      </label>
      <div className="guarded-change-actions">
        <button
          type="submit"
          className={tone === "danger" ? "danger-button" : "primary-button"}
          disabled={saving || outstanding || !reasonMet || !phraseMet}
        >
          {saving ? "Applying…" : submitLabel}
        </button>
        <button type="button" className="secondary-button" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
      <p className="guarded-change-note">Google will ask you to confirm your sign-in before this is applied.</p>
      <FormRequirements requirements={[...requirements, { met: reasonMet, label: "a reason of at least 3 characters" }, { met: phraseMet, label: `the exact phrase ${phrase}` }]} />
    </form>
  );
}
